/* ===================================================================
   useLocalFlag() -- a tiny boolean flag persisted to localStorage, scoped
   per client_id, for CLIENT-SIDE UI preferences that have no backend home.

   Used by Pass C overlays for two prefs the prototype kept in demo memory:
     - reminders-off  (the Finish-setup card's "turn off reminders" choice)
     - onboarding-seen (so the first-run overlay shows once for new accounts)

   These are UI dismissals, not product state -- persisting them locally is
   the honest, no-backend-needed behavior (mirrors how the app already keeps
   the theme in localStorage under 'rhody-theme'). Scoping by client_id keeps
   one operator's dismissals from leaking across tenants on a shared browser.
   =================================================================== */
import { useCallback, useState } from 'react';

function keyFor(name: string, scope: string | undefined): string {
  return `rhody:${name}:${scope ?? 'anon'}`;
}

function read(name: string, scope: string | undefined): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(keyFor(name, scope)) === '1';
  } catch {
    return false;
  }
}

export function useLocalFlag(
  name: string,
  scope: string | undefined,
): [boolean, (v: boolean) => void] {
  const [value, setValue] = useState(() => read(name, scope));

  const set = useCallback(
    (v: boolean) => {
      setValue(v);
      try {
        const k = keyFor(name, scope);
        if (v) window.localStorage.setItem(k, '1');
        else window.localStorage.removeItem(k);
      } catch {
        /* storage unavailable (private mode / quota) -- state still updates
           in memory for this session; nothing to persist. */
      }
    },
    [name, scope],
  );

  return [value, set];
}
